import { B as BaseComponent, _ as __decorate, a as __metadata } from '../index-Blpj1lVH.js';
import { css, html } from 'lit';
import { property, state, queryAssignedElements, customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import 'lit/directive.js';

/**
 * Roving Tabindex Controller
 *
 * Implements the ARIA roving tabindex pattern for keyboard navigation.
 * See: https://www.w3.org/WAI/ARIA/apg/practices/keyboard-interface/#kbd_roving_tabindex
 *
 * Usage:
 * ```ts
 * private rovingTabindex = new RovingTabindexController(this, {
 *   getItems: () => Array.from(this.querySelectorAll('my-item')),
 *   onSelect: (item) => this.selectItem(item),
 * });
 * ```
 */
class RovingTabindexController {
    constructor(host, config) {
        this.host = host;
        this.config = {
            wrap: true,
            direction: 'both',
            selectOnFocus: false,
            selectOnHomeEnd: false,
            skipItem: (item) => item.hasAttribute('disabled') || item.disabled === true,
            ...config,
        };
        host.addController(this);
    }
    // eslint-disable-next-line class-methods-use-this
    hostConnected() {
        // Controller is ready when host connects
    }
    // eslint-disable-next-line class-methods-use-this
    hostDisconnected() {
        // Cleanup if needed
    }
    /**
     * Update tabindex values for all items
     * @param activeIndex - Index of the item that should be tabbable (default: 0 or first non-disabled)
     */
    updateTabindices(activeIndex) {
        const items = this.config.getItems();
        if (!items || items.length === 0) {
            return;
        }
        // Find the active index
        let targetIndex = activeIndex ?? 0;
        // If no active index specified, use first non-disabled item
        if (activeIndex === undefined) {
            targetIndex = items.findIndex((item) => !this.config.skipItem(item));
            if (targetIndex === -1) {
                targetIndex = 0; // Fallback to first item if all disabled
            }
        }
        // Set tabindex for all items
        items.forEach((item, index) => {
            const itemElement = item;
            if (this.config.skipItem(itemElement)) {
                // eslint-disable-next-line no-param-reassign
                itemElement.tabIndex = -1;
            }
            else {
                // eslint-disable-next-line no-param-reassign
                itemElement.tabIndex = index === targetIndex ? 0 : -1;
            }
        });
    }
    /**
     * Handle keyboard navigation
     * @param currentItem - The currently focused item
     * @param event - The keyboard event
     * @returns true if the event was handled, false otherwise
     */
    handleKeydown(currentItem, event) {
        const direction = typeof this.config.direction === 'function' ? this.config.direction() : this.config.direction;
        const { key } = event;
        // Determine if this key should be handled based on direction
        const isVertical = key === 'ArrowUp' || key === 'ArrowDown';
        const isHorizontal = key === 'ArrowLeft' || key === 'ArrowRight';
        const isHome = key === 'Home';
        const isEnd = key === 'End';
        const shouldHandle = isHome ||
            isEnd ||
            (direction === 'both' && (isVertical || isHorizontal)) ||
            (direction === 'vertical' && isVertical) ||
            (direction === 'horizontal' && isHorizontal);
        if (!shouldHandle) {
            return false;
        }
        // Prevent default behavior (page scrolling)
        event.preventDefault();
        const items = this.config.getItems();
        const currentIndex = items.indexOf(currentItem);
        if (currentIndex === -1) {
            return false;
        }
        let nextIndex = currentIndex;
        // Handle Home/End keys
        if (isHome) {
            nextIndex = 0;
        }
        else if (isEnd) {
            nextIndex = items.length - 1;
        }
        else {
            // Handle arrow keys
            const isNext = key === 'ArrowDown' || key === 'ArrowRight';
            const isPrev = key === 'ArrowUp' || key === 'ArrowLeft';
            if (isNext) {
                nextIndex = this.getNextIndex(items, currentIndex, 1);
            }
            else if (isPrev) {
                nextIndex = this.getNextIndex(items, currentIndex, -1);
            }
        }
        // Skip disabled items — Home always searches forward, End always searches backward
        // eslint-disable-next-line no-nested-ternary
        const searchDirection = isHome ? 1 : isEnd ? -1 : nextIndex > currentIndex ? 1 : -1;
        nextIndex = this.findNextValidIndex(items, nextIndex, searchDirection);
        if (nextIndex !== -1 && nextIndex !== currentIndex) {
            const nextItem = items[nextIndex];
            // Update tabindices
            this.updateTabindices(nextIndex);
            // Focus the next item
            nextItem.focus();
            // Optionally select/activate the item.
            // Select when `selectOnFocus` is enabled, or when Home/End keys
            // were used and the consumer explicitly opted in via `selectOnHomeEnd`.
            const shouldSelect = this.config.selectOnFocus === true || ((isHome || isEnd) && this.config.selectOnHomeEnd === true);
            if (shouldSelect && this.config.onSelect) {
                this.config.onSelect(nextItem, event);
            }
            return true;
        }
        return false;
    }
    /**
     * Get the next index based on direction
     */
    getNextIndex(items, currentIndex, direction) {
        const { wrap } = this.config;
        let nextIndex = currentIndex + direction;
        if (wrap) {
            // Wrap around
            if (nextIndex < 0) {
                nextIndex = items.length - 1;
            }
            else if (nextIndex >= items.length) {
                nextIndex = 0;
            }
        }
        else {
            // Clamp to bounds
            nextIndex = Math.max(0, Math.min(items.length - 1, nextIndex));
        }
        return nextIndex;
    }
    /**
     * Find the next valid (non-disabled) index
     */
    findNextValidIndex(items, startIndex, direction) {
        const maxAttempts = items.length;
        let attempts = 0;
        let index = startIndex;
        while (attempts < maxAttempts) {
            if (!this.config.skipItem(items[index])) {
                return index;
            }
            index = this.getNextIndex(items, index, direction);
            attempts += 1;
        }
        // All items are disabled
        return -1;
    }
    /**
     * Set focus to a specific item
     */
    focusItem(item) {
        const items = this.config.getItems();
        const index = items.indexOf(item);
        if (index !== -1) {
            this.updateTabindices(index);
            item.focus();
        }
    }
    /**
     * Set focus to the first non-disabled item
     */
    focusFirst() {
        const items = this.config.getItems();
        const firstValidIndex = this.findNextValidIndex(items, 0, 1);
        if (firstValidIndex !== -1) {
            this.focusItem(items[firstValidIndex]);
        }
    }
    /**
     * Set focus to the last non-disabled item
     */
    focusLast() {
        const items = this.config.getItems();
        const lastValidIndex = this.findNextValidIndex(items, items.length - 1, -1);
        if (lastValidIndex !== -1) {
            this.focusItem(items[lastValidIndex]);
        }
    }
}

/**
 * Dispatches a cancelable custom event and runs the provided default action
 * only if the consumer did not call `event.preventDefault()`.
 *
 * This is the shared convention used across components (Tabs, Modal, Chip, Alert,
 * Forward, Back) to provide a sensible default JavaScript behavior out of the box
 * while letting consumers intercept and override it. Notification deliberately does
 * NOT use this: its show/close are always caller-initiated (no default markup-driven
 * trigger to override), so there was no real use case for blocking them — see
 * it-notification.ts for the plain (non-cancelable) events it fires instead.
 *
 * ```js
 * el.addEventListener('it-alert-close', (e) => {
 *   e.preventDefault();                    // block the default removal
 *   fetchConfirmation().then((ok) => {     // async logic runs after prevention
 *     if (ok) e.target.close();
 *   });
 * });
 * ```
 *
 * Note: `preventDefault()` must be called synchronously inside the listener.
 * Async code can run afterward; call the component's public method when ready.
 *
 * @param host the element dispatching the event
 * @param name the event name (kebab-case, `it-` prefixed)
 * @param detail the event `detail` payload
 * @param defaultAction the behavior executed when the event is not prevented
 * @returns the dispatched event (inspect `defaultPrevented` if needed)
 */
function dispatchCancelable(host, name, detail, defaultAction) {
    const event = new CustomEvent(name, {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail,
    });
    host.dispatchEvent(event);
    if (!event.defaultPrevented) {
        defaultAction();
    }
    return event;
}

var styles = css`.nav {
  --bsi-nav-link-disabled-color: var(--bsi-color-text-disabled);
  --bsi-nav-link-padding-x: var(--bsi-spacing-s);
  --bsi-nav-link-padding-y: var(--bsi-spacing-s);
}

/* stylelint-disable-next-line no-duplicate-selectors */
.nav {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0;
  padding-left: 0;
  list-style: none;
}

.nav-link {
  display: block;
  color: var(--bsi-navbar-link-color);
  font-size: var(--bsi-navbar-link-font-size);
  font-weight: var(--bsi-navbar-link-font-weight);
  line-height: var(--bsi-navbar-link-line-height);
  text-decoration: none;
}
.nav-link:hover {
  color: var(--bsi-navbar-link-color);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.nav-link.disabled {
  opacity: var(--bsi-navbar-disable-item-opacity);
  cursor: not-allowed;
  pointer-events: none;
}

.nav-pills {
  --bsi-nav-pills-border-radius: 4px;
  --bsi-nav-pills-link-active-bg: hsl(210, 100%, 40%);
  --bsi-nav-pills-link-active-color: hsl(0, 0%, 100%);
}
.nav-pills .nav-link {
  border: 0;
  background: none;
  border-radius: var(--bsi-nav-pills-border-radius);
}
.nav-pills .nav-link:disabled {
  border-color: transparent;
  background-color: transparent;
  color: var(--bsi-nav-link-disabled-color);
}
.nav-pills .nav-link.active,
.nav-pills .show > .nav-link {
  color: var(--bsi-nav-pills-link-active-color);
  background-color: var(--bsi-nav-pills-link-active-bg);
}

.nav-fill > .nav-link,
.nav-fill .nav-item {
  flex: 1 1 auto;
  text-align: center;
}

.nav-justified > .nav-link,
.nav-justified .nav-item {
  flex-basis: 0;
  flex-grow: 1;
  text-align: center;
}

.nav-fill .nav-item .nav-link,
.nav-justified .nav-item .nav-link {
  width: 100%;
}

.tab-content > .tab-pane {
  display: none;
}
.tab-content > .active {
  display: block;
}

.nav-tabs {
  --bsi-tabs-background-color: var(--bsi-color-background-inverse);
  --bsi-tabs-border-color: var(--bsi-color-border-subtle);
  --bsi-tab-text-color: var(--bsi-color-text-secondary);
  --bsi-tab-icon-color: var(--bsi-icon-secondary);
  --bsi-tab-border-radius: var(--bsi-radius-smooth);
  --bsi-tab-border-width: 3px;
  --bsi-tab-padding-x: var(--bsi-spacing-m);
  --bsi-tab-padding-y: var(--bsi-spacing-s);
  --bsi-tab-font-size: var(--bsi-label-font-size);
  --bsi-tab-font-weight: 600;
  --bsi-tab-shadow: 0 12px 25px -20px rgba(0, 0, 0, 0.5);
  --bsi-tab-background-light: var(--bsi-color-background-primary-lighter);
  --bsi-tab-border-color: transparent;
  --bsi-tab-border-width-cards: 2px;
  --bsi-tab-border-color-cards: var(--bsi-color-border-subtle);
  --bsi-tab-add-btn-border: var(--bsi-color-border-primary);
  --bsi-tab-add-btn-color: var(--bsi-color-background-primary);
  --bsi-tab-close-btn-color: var(--bsi-color-text-secondary);
  --bsi-tab-close-btn-color-hover: color-mix(
    in srgb,
    var(--bsi-primary) 90%,
    black 10%
  );
  --bsi-tab-close-btn-color-disabled: var(--bsi-color-text-disabled);
}

/* stylelint-disable-next-line no-duplicate-selectors */
.nav-tabs {
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  border-bottom-color: var(--bsi-tabs-border-color);
  background-color: var(--bsi-tabs-background-color);
  -webkit-overflow-scrolling: touch;
}
.nav-tabs .it-ico:before,
.nav-tabs .it-ico-lg:before {
  margin-right: 0;
}
.nav-tabs.auto .nav-link {
  flex: 1;
}
.nav-tabs.auto .nav-item {
  flex: 1;
  text-align: center;
}
.nav-tabs.no-border {
  border: none !important;
}
.nav-tabs.no-background {
  background-color: transparent;
}
.nav-tabs.no-background .nav-item .nav-link.active {
  background-color: transparent;
}
.nav-tabs.nav-tabs-icon-text .icon {
  margin-right: 0.5rem;
}
.nav-tabs .nav-link {
  display: flex;
  flex: 1 0 auto;
  align-items: center;
  justify-content: center;
  padding: var(--bsi-tab-padding-y) var(--bsi-tab-padding-x);
  background-color: var(--bsi-tab-background-color, transparent);
  border-width: var(--bsi-tab-border-width);
  border-style: solid;
  border-left-width: 0;
  border-top-width: 0;
  border-right-width: 0;
  border-color: transparent;
  border-bottom-width: var(--bsi-tab-border-width);
  border-radius: 0;
  color: var(--bsi-tab-text-color);
  font-size: var(--bsi-tab-font-size);
  font-weight: var(--bsi-tab-font-weight);
  line-height: 1rem;
  text-decoration: none;
  white-space: nowrap;
}
@media (min-width: 992px) {
  .nav-tabs .nav-link {
    --bsi-tab-font-size: var(--bsi-label-font-size-l);
  }
}
.nav-tabs .nav-link:hover {
  --bsi-tab-text-color: var(--bsi-color-text-secondary-hover);
}
.nav-tabs .nav-link.disabled {
  --bsi-tab-text-color: var(--bsi-color-link-disabled);
  cursor: not-allowed;
}
.nav-tabs .nav-link[data-focus-mouse=true]:not(.active) {
  border-color: var(--bsi-tab-border-color) !important;
  --bsi-tab-border-color: var(--bsi-color-border-subtle);
}
.nav-tabs .nav-link.active, .nav-tabs .nav-link[data-focus-mouse=true]:has(.active), .nav-tabs .nav-link[data-focus-mouse=true]:not(:focus) {
  --bsi-tab-border-color: var(--bsi-color-border-primary);
  --bsi-tab-text-color: var(--bsi-color-text-primary);
  border-color: var(--bsi-tab-border-color) !important;
}
.nav-tabs .nav-link.active .icon, .nav-tabs .nav-link[data-focus-mouse=true]:has(.active) .icon, .nav-tabs .nav-link[data-focus-mouse=true]:not(:focus) .icon {
  --bsi-tab-icon-color: var(--bsi-icon-primary);
}
.nav-tabs .nav-link:focus:not([data-focus-mouse=true]) {
  box-shadow: inset 0 0 0 2px var(--bsi-color-outline-focus) !important;
}
.nav-tabs .nav-link .icon {
  fill: var(--bsi-tab-icon-color);
}
.nav-tabs .nav-link .icon:hover {
  --bsi-tab-icon-color: var(--bsi-icon-secondary-hover);
}
.disabled .nav-tabs .nav-link .icon {
  fill: var(--bsi-icon-disabled);
}
.nav-tabs .nav-item {
  display: flex;
  margin-bottom: 0;
}
.nav-tabs.nav-tabs-vertical {
  flex-direction: column;
  border-color: var(--bsi-tabs-border-color);
  border-right-width: 1px;
  border-style: solid;
  border-bottom-width: 0;
  border-left-width: 0;
  border-top-width: 0;
}
.nav-tabs.nav-tabs-vertical .nav-link {
  position: relative;
  justify-content: space-between;
  border-right-width: var(--bsi-tab-border-width);
  border-bottom-width: 0;
  white-space: normal;
}
.nav-tabs.nav-tabs-vertical .nav-link.justify-content-end .it-ico:before,
.nav-tabs.nav-tabs-vertical .nav-link.justify-content-end .it-ico-lg:before {
  margin-right: 0;
}
.nav-tabs.nav-tabs-vertical.nav-tabs-vertical-background .nav-link.active,
.nav-tabs.nav-tabs-vertical.nav-tabs-vertical-background .nav-item.show .nav-link {
  --bsi-tab-background-color: var(--bsi-color-background-primary-lighter);
}
.nav-tabs.nav-dark {
  --bsi-tabs-border-color: var(--bsi-color-border-subtle);
  --bsi-tabs-background-color: var(--bsi-color-background-secondary-deep);
}
.nav-tabs.nav-dark .nav-link {
  --bsi-tab-text-color: var(--bsi-color-text-inverse);
  opacity: 0.75;
}
.nav-tabs.nav-dark .nav-link:hover {
  opacity: 1;
}
.nav-tabs.nav-dark .nav-link.disabled {
  opacity: 0.5;
}
.nav-tabs.nav-dark .nav-link .icon {
  --bsi-tab-icon-color: var(--bsi-color-text-inverse);
}
.nav-tabs.nav-dark .nav-link.active,
.nav-tabs.nav-dark .nav-item.show .nav-link {
  opacity: 1;
  --bsi-tab-border-color: var(--bsi-color-border-inverse);
}
.nav-tabs.nav-dark.nav-tabs-vertical .nav-link.active,
.nav-tabs.nav-dark.nav-tabs-vertical .nav-item.show .nav-link {
  border-bottom: none;
  border-right-color: var(--bsi-color-border-inverse);
}
.nav-tabs.nav-tabs-cards::after {
  content: "";
  flex-grow: 1;
  border-bottom: 1px solid var(--bsi-color-border-subtle);
}
.nav-tabs.nav-tabs-cards .nav-link {
  --bsi-tab-border-width: 1px;
  border-bottom: var(--bsi-tab-border-width) solid var(--bsi-color-border-subtle);
  position: relative;
}
.nav-tabs.nav-tabs-cards .nav-link[data-focus-mouse=true]:not(.active) {
  border-color: transparent;
}
.nav-tabs.nav-tabs-cards .nav-link.active, .nav-tabs.nav-tabs-cards .nav-link[data-focus-mouse=true]:not(:focus) {
  --bsi-tab-border-color: var(--bsi-color-border-subtle);
  border-color: var(--bsi-tab-border-color);
  border-top-width: var(--bsi-tab-border-width);
  border-right-width: var(--bsi-tab-border-width);
  border-left-width: var(--bsi-tab-border-width);
  border-bottom-color: transparent !important;
  border-top-left-radius: var(--bsi-tab-border-radius);
  border-top-right-radius: var(--bsi-tab-border-radius);
}
.nav-tabs.nav-tabs-editable .nav-item {
  position: relative;
  border-bottom: 0;
}
.nav-tabs.nav-tabs-editable .nav-item:last-of-type {
  padding: var(--bsi-tab-padding-y) calc(var(--bsi-tab-padding-x) / 2);
  border-bottom: 1px solid var(--bsi-color-border-subtle);
}
.nav-tabs.nav-tabs-editable .nav-tab-add {
  position: relative;
  width: 1.5rem;
  height: 1.5rem;
  margin: -0.2em 1em 0;
  border: 1px solid var(--bsi-color-border-primary);
  border-radius: 50%;
}
.nav-tabs.nav-tabs-editable .nav-tab-add:after {
  position: absolute;
  top: 0.278rem;
  left: 0.611rem;
  content: "";
  width: 2px;
  height: 0.778rem;
  background-color: var(--bsi-color-background-primary);
}
.nav-tabs.nav-tabs-editable .nav-tab-add:before {
  position: absolute;
  top: 0.611rem;
  left: 0.278rem;
  content: "";
  width: 0.778rem;
  height: 2px;
  background-color: var(--bsi-color-background-primary);
}
.nav-tabs.nav-tabs-editable .nav-link {
  padding-right: 3rem;
}
.nav-tabs.nav-tabs-editable .nav-link-close {
  position: absolute;
  top: calc(50% - 1rem);
  right: 0.889rem;
  color: var(--bsi-color-text-secondary);
  transition: color 0.2s;
  cursor: pointer;
}
.nav-tabs.nav-tabs-editable .nav-link-close .icon {
  fill: var(--bsi-icon-secondary);
}
.nav-tabs.nav-tabs-editable .nav-link-close.disabled {
  color: var(--bsi-color-text-disabled);
  cursor: pointer;
}
.nav-tabs.nav-tabs-editable .nav-link-close.disabled .icon {
  fill: var(--bsi-icon-disabled);
}
.nav-tabs.nav-tabs-editable .nav-link-close:hover {
  color: shade-color(hsl(210, 100%, 40%), 10%);
}
.nav-tabs.nav-tabs-editable .nav-link-close:hover .icon {
  fill: shade-color(hsl(210, 100%, 40%), 10%);
}
.nav-tabs.nav-tabs-editable .nav-link-close .it-ico {
  font-size: 0.625rem;
}
.nav-tabs.nav-tabs-editable .nav-link-close .it-ico:before {
  margin-right: 0;
}
.nav-tabs.nav-tabs-editable .nav-link.active .nav-link-close,
.nav-tabs.nav-tabs-editable .nav-item.show .nav-link .nav-link-close {
  color: var(--bsi-color-text-primary);
}
.nav-tabs.nav-tabs-editable .nav-link.active .nav-link-close .icon,
.nav-tabs.nav-tabs-editable .nav-item.show .nav-link .nav-link-close .icon {
  fill: var(--bsi-icon-primary);
}

.flex-column-reverse .nav-tabs {
  border-top-width: 1px;
  border-top-style: solid;
  border-top-color: var(--bsi-color-border-subtle);
  border-bottom-width: 0;
}
.flex-column-reverse .nav-tabs .nav-link {
  border-top-width: var(--bsi-tab-border-width);
  border-bottom-width: 0;
}

.flex-row-reverse .nav-tabs.nav-tabs-vertical {
  border-right-width: 0;
  border-left-width: 1px;
}
.flex-row-reverse .nav-tabs.nav-tabs-vertical .nav-link {
  justify-content: flex-start;
  border-right-width: 0;
  border-bottom-width: 0;
  border-left-width: var(--bsi-tab-border-width);
}

.nav-tabs-hidescroll {
  height: 2.667rem;
  overflow: hidden;
}
.nav-tabs-hidescroll.hidescroll-ico {
  height: 2.944rem;
}
.nav-tabs-hidescroll.hidescroll-ico-lg {
  height: 3.444rem;
}
.nav-tabs-hidescroll .nav-tabs {
  padding-bottom: 20px;
}

@media (max-width: 767.98px) {
  .nav-tabs {
    box-shadow: 0 12px 25px -20px rgba(0, 0, 0, 0.5);
  }
  .nav-tabs.nav-tabs-vertical, .nav-tabs.nav-tabs-cards {
    box-shadow: none;
  }
}
@media (min-width: 1200px) {
  .nav-tabs {
    flex-wrap: wrap;
    overflow-x: hidden;
  }
  .nav-tabs.auto {
    flex-wrap: nowrap;
  }
  .nav-tabs-hidescroll {
    height: auto;
    overflow: initial;
  }
  .nav-tabs-hidescroll.hidescroll-ico, .nav-tabs-hidescroll.hidescroll-ico-lg {
    height: auto;
  }
  .nav-tabs-hidescroll .nav-tabs {
    padding-bottom: 0;
  }
}
:host {
  display: flex;
  width: 100%;
  height: 100%;
}

::slotted([slot=after-tablist]) {
  flex-shrink: 0;
  align-self: center;
  margin-inline-start: 0.25rem;
}

.tabs-wrapper {
  display: flex;
  max-width: 100%;
  flex: 1;
  flex-direction: var(--it-tab_wrapper-flex-dir);
  align-items: var(--it-tab_wrapper-align);
  margin-block: 0.75rem;
  --it-tab_wrapper-flex-dir: column;
  --it-tab_wrapper-align: stretch;
  --it-tab_wrapper-content-flex: initial;
}
.tabs-wrapper--vertical {
  --it-tab_wrapper-flex-dir: row;
  --it-tab_wrapper-content-flex: 1;
}
.tabs-wrapper--start {
  gap: 24px;
  --it-tab_wrapper-flex-dir: row;
  --it-tab_wrapper-content-flex: 1;
}
.tabs-wrapper--end {
  gap: 24px;
  --it-tab_wrapper-flex-dir: row-reverse;
  --it-tab_wrapper-content-flex: 1;
}

.nav-row {
  display: flex;
  align-items: center;
  overflow-y: visible;
}

.tabs-wrapper--vertical .nav-row,
.tabs-wrapper--start .nav-row,
.tabs-wrapper--end .nav-row {
  flex: 0 0 var(--it-tabs-nav-size, 30%);
  flex-direction: column;
  align-items: stretch;
}

.tab-content {
  min-width: 0;
  flex: var(--it-tab_wrapper-content-flex, initial);
  margin-top: var(--it-tab_content-mt, 0.5rem);
}
.tab-content ::slotted(:not(it-tab-panel)) {
  display: none;
}

.tabs-wrapper--vertical .tab-content,
.tabs-wrapper--start .tab-content,
.tabs-wrapper--end .tab-content {
  --it-tab_content-mt: 0;
}

.tabs-wrapper--bottom {
  flex-direction: column-reverse;
}

.tabs-wrapper--bottom .nav-tabs {
  order: 1;
  border-top-width: 1px;
  border-bottom-width: 0;
  margin-top: 0.75rem;
  --it-tab_bw-top: var(--bsi-tab-border-width);
  --it-tab_bw-bottom: 0;
  --it-tab_active-bc-top: var(--bsi-color-border-primary);
  --it-tab_active-bc-bottom: transparent;
  background-color: var(--it-tab_active-bg, var(--bsi-tabs-background-color));
}

.tabs-wrapper--vertical,
.tabs-wrapper--start,
.tabs-wrapper--end {
  --it-tab-panel_margin-block: -0.75rem;
}

.nav-tabs-vertical {
  height: 100%;
  flex: 1;
  --it-tab_bw-right: var(--bsi-tab-border-width);
  --it-tab_bw-bottom: 0;
  --it-tab_active-bc-right: var(--bsi-color-border-primary);
  --it-tab_active-bc-bottom: transparent;
  --it-tab_justify: flex-start;
  --it-tab_white-space: normal;
}

.tabs-wrapper--end .nav-tabs-vertical {
  border-right-width: 0;
  border-left-width: 1px;
  --it-tab_bw-right: 0;
  --it-tab_bw-left: var(--bsi-tab-border-width);
  --it-tab_active-bc-right: transparent;
  --it-tab_active-bc-left: var(--bsi-color-border-primary);
}

.nav-tabs-vertical-background {
  --it-tab_active-bg: var(--bsi-tab-background-light);
}

.nav-dark {
  --bsi-tab-text-color: var(--bsi-color-text-inverse);
  --bsi-tab-icon-color: var(--bsi-color-text-inverse);
  --it-tab_opacity: 0.75;
  --it-tab_hover-text-color: var(--bsi-color-text-inverse);
  --it-tab_hover-icon-color: var(--bsi-color-text-inverse);
  --it-tab_color-active: var(--bsi-color-text-inverse);
  --it-tab_icon-color-active: var(--bsi-color-text-inverse);
  --it-tab_active-bc-top: var(--bsi-color-border-inverse);
  --it-tab_active-bc-right: var(--bsi-color-border-inverse);
  --it-tab_active-bc-bottom: var(--bsi-color-border-inverse);
  --it-tab_active-bc-left: var(--bsi-color-border-inverse);
}

.nav-tabs-cards {
  overflow: visible;
  align-items: flex-end;
  border-bottom: 1px solid var(--bsi-color-border-subtle);
}

.nav-tabs-icon-text {
  --it-tab_gap: var(--bsi-spacing-xs, 0.25rem);
}

.nav-tabs {
  overflow-x: visible !important;
  overflow-y: unset !important;
}

.nav-tabs.nav-tabs-cards::after {
  content: none;
}

.nav-row.auto {
  flex-wrap: nowrap;
  justify-content: space-between;
}
.nav-row.auto > .nav-tabs {
  flex: 1;
  overflow-x: auto !important;
}

@media (max-width: 767.98px) {
  .nav-row.auto {
    box-shadow: 0 12px 25px -20px rgba(0, 0, 0, 0.5);
  }
  .nav-row.auto > .nav-tabs {
    box-shadow: none !important;
  }
}
.nav-row.nav-tabs-hidescroll > .nav-tabs {
  -ms-overflow-style: none; /* IE / Edge */
  overflow-x: auto !important;
  scrollbar-width: none; /* Firefox */
}

.nav-row.nav-tabs-hidescroll > .nav-tabs::-webkit-scrollbar {
  display: none; /* Chrome, Safari, WebKit */
}

.nav-tabs-hidescroll {
  overflow: hidden;
  height: unset !important;
}

.nav-tabs-hidescroll .nav-tabs {
  padding-bottom: 0 !important;
}`;

/**
 * `it-tabs` è il componente contenitore per un'interfaccia a schede (tab).
 *
 * Gestisce la selezione, la navigazione da tastiera (roving tabindex, frecce, Home, End)
 * e i collegamenti ARIA tra trigger e pannelli.
 *
 * ## Struttura attesa
 *
 * ```html
 * <it-tabs label="Navigazione">
 *   <it-tab slot="tab" panel="p1">Tab 1</it-tab>
 *   <it-tab slot="tab" panel="p2">Tab 2</it-tab>
 *
 *   <it-tab-panel name="p1">Contenuto del primo pannello</it-tab-panel>
 *   <it-tab-panel name="p2">Contenuto del secondo pannello</it-tab-panel>
 * </it-tabs>
 * ```
 *
 * @slot tab  - Slot nominato per gli elementi `it-tab` (trigger della lista)
 * @slot after-tablist - Slot opzionale per elementi aggiuntivi dopo la lista di tab
 *   (es. pulsante "aggiungi"). Appare alla fine della tablist visivamente allineato ai tab.
 * @slot      - Slot di default per gli elementi `it-tab-panel` (contenuto)
 *
 * @cssprop [--it-tabs-nav-size=auto] - Larghezza (flex-basis) della tablist in modalità
 *   verticale (`placement=start|end`). Accetta qualsiasi valore CSS valido
 *   per `flex-basis` (es. `200px`, `25%`, `12rem`). Non ha effetto in modalità orizzontale.
 *
 * @fires it-tabs-change - Emesso quando cambia il pannello attivo.
 *   `detail.panel` contiene il nome del pannello attivato.
 * @fires it-tab-close - Emesso (quando `dismissible` è attivo) quando si clicca il pulsante ×,
 *   si preme Delete/Backspace, o si ri-attiva il tab già selezionato (doppio tap mobile SR).
 *   Il `detail` è tipizzato come {@link ItTabCloseEventDetail}: `detail.panel` e `detail.type`.
 *
 *   L'evento è **cancelable**: chiamare `e.preventDefault()` blocca la rimozione automatica.
 *   Per eseguire la chiusura standard in un momento a scelta (es. dopo conferma asincrona)
 *   richiamare il metodo pubblico `itTabs.close(e.detail.panel)`.
 */
let ItTabs = class ItTabs extends BaseComponent {
    constructor() {
        super(...arguments);
        /**
         * Testo dell'`aria-label` per la tablist.
         * Obbligatorio quando non è presente un'intestazione visiva adiacente.
         */
        this.label = '';
        /**
         * Tab a tutta larghezza: i tab occupano l'intera larghezza disponibile.
         * Corrisponde alla classe Bootstrap Italia `.auto` sulla tablist.
         */
        this.auto = false;
        /**
         * Nasconde visivamente la scrollbar orizzontale della tablist su viewport intermedi
         * (≥ 768px e < 1200px), dove Bootstrap Italia mostra normalmente il tab overflow con scrollbar.
         * Su mobile (< 768px) e su desktop wide (≥ 1200px) il meccanismo viene disattivato automaticamente.
         *
         * Il meccanismo BSI: il wrapper `.nav-row` riceve `overflow: hidden` + altezza fissa che
         * stoppa la scrollbar; `.nav-tabs` riceve `padding-bottom: 20px` che spinge il
         * track della scrollbar sotto il bordo di clip, rendendola invisibile ma preservando lo scroll.
         *
         * Corrisponde alla classe Bootstrap Italia `.nav-tabs-hidescroll` sul wrapper.
         * Ignorato nei layout verticali (`placement="start"` / `placement="end"`) e con `cards`.
         * Usato tipicamente insieme ad `auto`.
         */
        this.hideScrollbar = false;
        /**
         * Sfondo primario chiaro sul tab selezionato (solo in layout verticale: `placement="start"` o `placement="end"`).
         * Corrisponde alla classe Bootstrap Italia `.nav-tabs-vertical-background`.
         */
        this.verticalBackground = false;
        /**
         * Variante con sfondo scuro per la tablist.
         * Corrisponde alla classe Bootstrap Italia `.nav-dark`.
         * Ignorato quando `cards` è attivo: le due varianti non sono compatibili.
         */
        this.dark = false;
        /**
         * Stile "card" per i tab.
         * Corrisponde alla classe Bootstrap Italia `.nav-tabs-cards`.
         */
        this.cards = false;
        /**
         * Abilita la chiusura dei tab tramite il pulsante × e i tasti Delete/Backspace.
         * Indipendente da `cards`: può essere usato con qualsiasi variante di tab.
         * Quando attivo, ogni `it-tab` mostra il pulsante × e risponde
         * ai tasti Delete/Backspace con l'evento cancelable `it-tab-close`.
         */
        this.dismissible = false;
        /**
         * Attiva l'animazione "a comparsa" (fade in/out) sui pannelli.
         * Equivale alla classe `.fade` di Bootstrap Italia su ogni `.tab-pane`.
         */
        this.fade = false;
        /**
         * Posizione della tablist rispetto ai pannelli.
         * - `top` (default): tablist sopra il contenuto
         * - `bottom`: tablist sotto il contenuto
         * - `start`: tablist a sinistra (layout verticale)
         * - `end`: tablist a destra (layout verticale)
         */
        this.placement = 'top';
        // ─── Internal state ───────────────────────────────────────────────────────
        /**
         * Traccia se almeno uno dei tab contiene un elemento `it-icon`.
         * Determina se applicare la classe `.nav-tabs-icon-text` per ottimizzare
         * lo spazio tra icona e testo.
         */
        this._hasIcons = false;
        /**
         * Roving tabindex controller: gestisce tabindex e navigazione da tastiera
         * tra gli elementi `it-tab` secondo il pattern WAI-ARIA.
         *
         * La direzione è dinamica: `'horizontal'` per layout orizzontali,
         * `'vertical'` per layout verticali (placement start/end).
         */
        this.rovingTabindex = new RovingTabindexController(this, {
            getItems: () => this._tabs,
            onSelect: (tab) => this._selectTab(tab),
            selectOnFocus: true,
            // Home/End should activate tabs per WAI-ARIA for this component.
            selectOnHomeEnd: true,
            direction: 'both',
            skipItem: (tab) => tab.disabled,
        });
        /**
         * Intercetta `it-tab-close-request` emesso dal bottone × nello shadow DOM di `it-tab`.
         * Prima di dispatchiare `it-tab-close` all'esterno, sposta il focus su un tab adiacente
         * in modo che l'utente da tastiera non perda il punto di navigazione.
         */
        this._onTabCloseRequest = (e) => {
            const tab = this._tabs.find((t) => t.panel === e.detail.panel);
            if (tab)
                this._closeTabWithFocusShift(tab, e.detail.type !== 'click');
        };
        // ─── Event handlers ──────────────────────────────────────────────────────
        this._onTabSelect = (e) => {
            const target = e.target;
            if (target.tagName.toLowerCase() === 'it-tab') {
                this._selectTab(target);
            }
        };
        /**
         * Navigazione da tastiera nella tablist (WAI-ARIA tabs pattern).
         *
         * Orizzontale: ArrowLeft / ArrowRight / Home / End
         * Verticale:   ArrowUp  / ArrowDown  / Home / End
         *
         * La logica di navigazione viene delegata al `RovingTabindexController`
         * che gestisce wrap-around, skip dei disabilitati e aggiornamento dei tabindex.
         */
        this._onKeyDown = (e) => {
            // Risale il composedPath per trovare l'it-tab che ha originato l'evento
            // (attraversa i confini dello shadow DOM).
            const path = e.composedPath();
            const currentTab = path.find((el) => el instanceof HTMLElement && el.tagName.toLowerCase() === 'it-tab');
            if (!currentTab || currentTab.disabled)
                return;
            // Delete/Backspace quando dismissible è attivo: sposta il focus su un tab adiacente,
            // poi notifica l'esterno di chiudere il tab corrente.
            if ((e.key === 'Delete' || e.key === 'Backspace') && this.dismissible) {
                e.preventDefault();
                this._closeTabWithFocusShift(currentTab, true);
                return;
            }
            // direction='both': tutte e 4 le frecce sono valide in ogni orientamento
            const validKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
            if (!validKeys.includes(e.key))
                return;
            // Delega navigazione + aggiornamento tabindex + selezione (via onSelect) al controller.
            this.rovingTabindex.handleKeydown(currentTab, e);
        };
    }
    // ─── Keyboard navigation ──────────────────────────────────────────────────
    /**
     * Determina se la tablist è in orientamento verticale.
     * Usato sia per il layout che per la direzione della navigazione da tastiera.
     */
    get _isVertical() {
        return this.placement === 'start' || this.placement === 'end';
    }
    // ─── Lifecycle ───────────────────────────────────────────────────────────
    connectedCallback() {
        super.connectedCallback?.();
        this.addEventListener('keydown', this._onKeyDown);
        this.addEventListener('it-tab-select', this._onTabSelect);
        this.addEventListener('it-tab-close-request', this._onTabCloseRequest);
    }
    disconnectedCallback() {
        this.removeEventListener('keydown', this._onKeyDown);
        this.removeEventListener('it-tab-select', this._onTabSelect);
        this.removeEventListener('it-tab-close-request', this._onTabCloseRequest);
        super.disconnectedCallback?.();
    }
    firstUpdated(changedProperties) {
        super.firstUpdated?.(changedProperties);
        this._initTabs();
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        // Re-sync tab state when dismissible (or cards/fade) change at runtime.
        if (changedProperties.has('dismissible') || changedProperties.has('cards') || changedProperties.has('fade')) {
            this._initTabs();
        }
    }
    // ─── Internal state management ───────────────────────────────────────────
    /**
     * Inizializza i tab: attiva il primo tab non disabilitato se nessuno è già attivo,
     * collega i tab ai pannelli via ARIA e allinea il roving tabindex controller.
     */
    _initTabs() {
        const tabs = this._tabs;
        const panels = this._panels;
        if (!tabs.length)
            return;
        // Rileva se almeno un tab contiene un elemento it-icon per applicare la classe icon-text
        this._hasIcons = tabs.some((tab) => tab.querySelector('it-icon') !== null);
        const alreadyActive = tabs.find((t) => t.active && !t.disabled);
        const toActivate = alreadyActive ?? tabs.find((t) => !t.disabled) ?? null;
        tabs.forEach((tab) => {
            const isActive = tab === toActivate;
            // eslint-disable-next-line no-param-reassign
            tab.active = isActive;
            const panel = panels.find((p) => p.name === tab.panel);
            if (panel) {
                if (!panel.id)
                    panel.id = panel.name;
                // Imposta aria-labelledby puntando al bottone interno del tab.
                // Cross-shadow ARIA id reference: funziona nei browser moderni (Chrome 90+, FF 100+, Safari 16+).
                panel.setAttribute('aria-labelledby', tab.tabId);
                // Il fade può essere impostato sull'it-tabs (propagato a tutti i pannelli)
                // o direttamente su singoli it-tab-panel. Non sovrascrivere il valore
                // già presente sul pannello se l'it-tabs non ha fade.
                // eslint-disable-next-line no-param-reassign
                panel.fade = panel.fade || this.fade;
                // eslint-disable-next-line no-param-reassign
                panel.active = isActive;
            }
            // Propaga la variante cards su ogni it-tab — CSS :host([cards]) può stilare direttamente.
            // eslint-disable-next-line no-param-reassign
            tab.cards = this.cards;
            // Abilita il pulsante × nello shadow DOM di it-tab quando dismissible è attivo.
            // Nessuna manipolazione del light DOM: sicuro per SSR e framework re-render.
            // eslint-disable-next-line no-param-reassign
            tab.dismissible = this.dismissible;
            // ARIA: shortcut keyboard Delete + Backspace se la chiusura è abilitata.
            // aria-keyshortcuts è supportato dai browser come attributo; l'annuncio
            // da parte degli screen reader è best-effort (varia per SR/versione).
            if (this.dismissible && !tab.disabled) {
                tab.setAttribute('aria-keyshortcuts', 'Delete Backspace');
            }
            else {
                tab.removeAttribute('aria-keyshortcuts');
            }
        });
    }
    /**
     * Sposta il focus sul tab adiacente (successivo, altrimenti precedente) e
     * dispatcha `it-tab-close` verso l'esterno.
     *
     * Perché NON usiamo `_selectTab` né `rovingTabindex.focusItem`:
     * - `_selectTab` dispatcha `it-tabs-change`, che può far reagire il consumer
     *   in modo inaspettato (re-render, rimozione sync, ecc.) prima che il focus
     *   sia spostato — causa il "salto" di un tab.
     * - `rovingTabindex.focusItem` chiama `getItems()` che potrebbe leggere
     *   una lista di tab parzialmente aggiornata se il DOM è cambiato tra
     *   `_selectTab` e la chiamata a `focusItem`.
     *
     * Approccio diretto:
     * 1. Se il tab da chiudere era attivo, aggiorniamo active + pannelli
     *    direttamente (senza eventi intermedi) — così `_initTabs` post-rimozione
     *    trova `alreadyActive = target` e non sceglie il primo della lista.
     * 2. `target.tabIndex = 0` + `target.focus()` — atomici, nessun `getItems()`
     *    intermedio che potrebbe restituire una lista stale.
     * 3. Dispatch `it-tab-close` — il consumer rimuove il tab.
     *
     * Pattern: WAI-ARIA APG tabs § keyboard Delete.
     */
    /**
     * Esegue la chiusura fisica senza emettere eventi:
     * trasferisce lo stato `active` al tab adiacente,
     * sposta il focus (solo se `moveFocus`) e rimuove dal DOM.
     *
     * Chiamato sia come default action di `_closeTabWithFocusShift`
     * sia direttamente dal metodo pubblico `close()`.
     */
    _executeClose(closingTab, moveFocus) {
        const tabs = this._tabs;
        const idx = tabs.indexOf(closingTab);
        const after = tabs.slice(idx + 1).find((t) => !t.disabled);
        const before = tabs
            .slice(0, idx)
            .reverse()
            .find((t) => !t.disabled);
        const target = after ?? before ?? null;
        if (target) {
            if (closingTab.active) {
                // Trasferisce lo stato active al target in modo sincrono,
                // così _initTabs (dopo lo slotchange) lo trova già attivo.
                tabs.forEach((t) => {
                    // eslint-disable-next-line no-param-reassign
                    t.active = t === target;
                });
                this._panels.forEach((p) => {
                    // eslint-disable-next-line no-param-reassign
                    p.active = p.name === target.panel;
                });
            }
            // Focus sincrono: tabIndex=0 garantisce che focus() funzioni anche se Lit
            // non ha ancora aggiornato il DOM (active → tabIndex avviene async in updated).
            // Avviene solo da tastiera (Delete/Backspace), non da click × o chiamata pubblica.
            if (moveFocus) {
                target.tabIndex = 0;
                target.focus();
            }
        }
        const panelEl = this._panels.find((p) => p.name === closingTab.panel);
        closingTab.remove();
        panelEl?.remove();
    }
    _closeTabWithFocusShift(closingTab, moveFocus) {
        dispatchCancelable(this, 'it-tab-close', { panel: closingTab.panel, type: moveFocus ? 'keydown' : 'click' }, () => this._executeClose(closingTab, moveFocus));
    }
    /**
     * Chiude il tab associato al pannello `panel`, trasferendo lo stato `active`
     * al tab adiacente e rimuovendo `it-tab` + `it-tab-panel` dal DOM.
     *
     * Metodo pubblico pensato per essere chiamato dopo aver intercettato
     * `it-tab-close` con `e.preventDefault()` — ad esempio al termine di
     * una conferma asincrona:
     *
     * ```js
     * itTabs.addEventListener('it-tab-close', async (e) => {
     *   e.preventDefault();
     *   if (await myModal.confirm('Chiudere?')) itTabs.close(e.detail.panel);
     * });
     * ```
     *
     * Noop se `dismissible` non è attivo o il pannello non esiste.
     * Non ri-emette `it-tab-close`.
     */
    close(panel) {
        if (!this.dismissible)
            return;
        const tab = this._tabs.find((t) => t.panel === panel);
        if (tab)
            this._executeClose(tab, false);
    }
    /**
     * Aggiunge un `it-tab` e il relativo `it-tab-panel` al componente.
     *
     * Il tab viene inserito prima dell'elemento `slot="after-tablist"` se presente
     * (in modo da restare all'interno della tablist prima del pulsante aggiungi),
     * altrimenti in fondo alla lista. Il pannello è sempre appeso in fondo.
     *
     * ```js
     * const tab = document.createElement('it-tab');
     * tab.setAttribute('slot', 'tab');
     * tab.setAttribute('panel', 'p5');
     * tab.textContent = 'Tab 5';
     * const panel = document.createElement('it-tab-panel');
     * panel.setAttribute('name', 'p5');
     * panel.textContent = 'Contenuto 5';
     * itTabs.addTab(tab, panel);
     * ```
     */
    addTab(tab, panel) {
        const afterTablist = this.querySelector('[slot="after-tablist"]');
        if (afterTablist) {
            this.insertBefore(tab, afterTablist);
        }
        else {
            this.appendChild(tab);
        }
        this.appendChild(panel);
    }
    /**
     * Seleziona il tab indicato e aggiorna tutti gli stati (tab attivo, pannello visibile,
     * tabindex sul roving controller).
     */
    _selectTab(targetTab) {
        if (targetTab.disabled)
            return;
        const tabs = this._tabs;
        const panels = this._panels;
        tabs.forEach((tab) => {
            const isTarget = tab === targetTab;
            // eslint-disable-next-line no-param-reassign
            tab.active = isTarget;
            const panel = panels.find((p) => p.name === tab.panel);
            if (panel) {
                // eslint-disable-next-line no-param-reassign
                panel.active = isTarget;
            }
        });
        this.dispatchEvent(new CustomEvent('it-tabs-change', {
            bubbles: true,
            composed: true,
            detail: { panel: targetTab.panel },
        }));
    }
    // ─── Slot change handlers ────────────────────────────────────────────────
    _onTabSlotChange() {
        this._initTabs();
    }
    _onPanelSlotChange() {
        this._initTabs();
    }
    // ─── Render ──────────────────────────────────────────────────────────────
    render() {
        const isVertical = this._isVertical;
        const tablistClasses = classMap({
            nav: true,
            'nav-tabs': true,
            auto: this.auto && !isVertical,
            'nav-dark': this.dark && !this.cards,
            'nav-tabs-vertical': isVertical,
            'nav-tabs-vertical-background': isVertical && this.verticalBackground,
            'nav-tabs-icon-text': this._hasIcons,
            'nav-tabs-cards': this.cards,
        });
        const rowClasses = classMap({
            'nav-row': true,
            auto: this.auto && !isVertical,
            'nav-tabs-hidescroll': this.hideScrollbar && !isVertical && !this.cards,
            // 'hidescroll-ico': this.hideScrollbar && this.iconText && !isVertical && !this.cards,
        });
        const wrapperClasses = classMap({
            'tabs-wrapper': true,
            'tabs-wrapper--bottom': this.placement === 'bottom',
            'tabs-wrapper--start': this.placement === 'start',
            'tabs-wrapper--end': this.placement === 'end',
            'tabs-wrapper--vertical': isVertical,
        });
        return html `
      <div class=${wrapperClasses}>
        <div class=${rowClasses}>
          <div
            class=${tablistClasses}
            role="tablist"
            part="tablist"
            aria-label=${ifDefined(this.label || undefined)}
            aria-orientation=${isVertical ? 'vertical' : 'horizontal'}
          >
            <slot name="tab" @slotchange=${this._onTabSlotChange}></slot>
          </div>
          <slot name="after-tablist"></slot>
        </div>
        <div class="tab-content" part="tab-content">
          <slot @slotchange=${this._onPanelSlotChange}></slot>
        </div>
      </div>
    `;
    }
};
ItTabs.styles = styles;
ItTabs.shadowRootOptions = { ...BaseComponent.shadowRootOptions, delegatesFocus: true };
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItTabs.prototype, "label", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItTabs.prototype, "auto", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-scrollbar', reflect: true }),
    __metadata("design:type", Object)
], ItTabs.prototype, "hideScrollbar", void 0);
__decorate([
    property({ type: Boolean, attribute: 'vertical-background', reflect: true }),
    __metadata("design:type", Object)
], ItTabs.prototype, "verticalBackground", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItTabs.prototype, "dark", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItTabs.prototype, "cards", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItTabs.prototype, "dismissible", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItTabs.prototype, "fade", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItTabs.prototype, "placement", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItTabs.prototype, "_hasIcons", void 0);
__decorate([
    queryAssignedElements({ slot: 'tab', selector: 'it-tab' }),
    __metadata("design:type", Array)
], ItTabs.prototype, "_tabs", void 0);
__decorate([
    queryAssignedElements({ selector: 'it-tab-panel' }),
    __metadata("design:type", Array)
], ItTabs.prototype, "_panels", void 0);
ItTabs = __decorate([
    customElement('it-tabs')
], ItTabs);

export { ItTabs };
//# sourceMappingURL=it-tabs.js.map
